import {
  startOfDay, format, addDays, subMilliseconds,
} from 'date-fns';
import { useQuery, useMutation, provideApolloClient } from '@vue/apollo-composable';
import { login as loginMutation } from '@graphql/login';
import { logout as logoutMutation } from '@graphql/logout';
import { me as meQuery } from '@graphql/me';
import { healthCheck } from '@graphql/healthCheck';
import { getTasks } from '@graphql/tasks/getTasks';
import { getListTypes } from '@graphql/listTypes/getListTypes';
import { apolloClient } from '@boot/vue-apollo';
import { createListItem } from '@graphql/listItems/createListItem';
import { toPlain } from '@utils/toPlain';
import { defaultUserModel } from '@const/defaultUserModel';
import { api } from '@boot/axios';
import { getOperationName } from '@apollo/client/utilities';
import { filterOperators } from '@const/filter';
import { taskStatus, taskStatusInt } from '@const/tasks';
import { getCurrentUserTimezoneOffset } from '@utils/dateFormat';
import { userRole } from 'src/const/userRoles';

provideApolloClient(apolloClient);

export async function login({ commit, dispatch }, { email, password }) {
  const res = await api.graphQLRequest(
    getOperationName(loginMutation),
    loginMutation.loc.source.body,
    {
      email,
      password,
    },
  );

  const user = res?.data?.data?.login?.user;
  const token = res?.data?.data?.login?.token;
  const error = res?.data?.errors?.length ? res?.data?.errors[0] : null;
  const hasAccess = res?.data?.data?.login?.user.roles.find((r) => [
    userRole.SUPERADMIN,
    userRole.STAFF,
    userRole.PROVIDER,
  ].includes(r.name));

  if (!user || !hasAccess) {
    throw new Error(error?.message || 'Login error');
  }

  commit('updateUser', {
    ...user,
    token,
  });

  // The login form reloads the page right after this, which would cancel the
  // request. A failure only leaves the list types empty, so it doesn't block login.
  await dispatch('fetchListTypes').catch(() => {});
}

export async function checkUserToken({ commit }) {
  try {
    const res = await api.graphQLRequest(
      getOperationName(meQuery),
      meQuery.loc.source.body,
    );
    const id = res.data?.data?.me?.id;
    if (id) {
      commit('notifications/setNetworkError', false, { root: true });
    }

    return !!id;
  } catch (e) {
    // Skip network errors in case if user is offline for some time
    if (e.message === 'Network Error') {
      commit('notifications/setNetworkError', true, { root: true });

      return true;
    }

    commit('updateUser', toPlain(defaultUserModel));

    return false;
  }
}

export async function checkAppHealth({ commit, dispatch }) {
  try {
    const res = await api.graphQLRequest(
      getOperationName(healthCheck),
      healthCheck.loc.source.body,
    );
    const isLive = res.data?.data?.healthCheck;
    if (isLive) {
      commit('notifications/setNetworkError', false, { root: true });
    }
  } catch (e) {
    if (e.message === 'Network Error') {
      commit('notifications/setNetworkError', true, { root: true });

      // If service is not live, recheck after 10 sec
      setTimeout(() => {
        dispatch('checkAppHealth');
      }, 1000 * 10);
    }
  }
}

export async function getTodayTasks({ commit }) {
  const now = new Date();
  const startOfUserDay = subMilliseconds(startOfDay(now), getCurrentUserTimezoneOffset(now));
  const endOfUserDay = addDays(startOfUserDay, 1);
  const filterDateformat = 'yyyy-MM-dd\'T\'HH:mm:ss\'.000Z\'';
  const startOfUnixUtcString = format(new Date(0), filterDateformat);
  const endOfUserDayUtcString = format(endOfUserDay, filterDateformat);

  try {
    const res = await api.graphQLRequest(
      getOperationName(getTasks),
      getTasks.loc.source.body,
      {
        first: 100,
        where: {
          AND: [
            {
              column: 'STATUS',
              operator: filterOperators.EQ,
              value: taskStatusInt[taskStatus.OPEN],
            },
            {
              column: 'DUE_DATE',
              operator: filterOperators.BETWEEN,
              value: [startOfUnixUtcString, endOfUserDayUtcString],
            },
          ],
        },
      },
    );
    const tasks = res.data?.data?.tasks?.data;
    const count = res.data?.data?.tasks?.paginatorInfo?.total;
    if (tasks) {
      commit('updateTodayTasks', { tasks, count });
    }

    return tasks;
  } catch (e) {
    return null;
  }
}

export async function logout({ commit }) {
  try {
    await api.graphQLRequest(
      getOperationName(logoutMutation),
      logoutMutation.loc.source.body,
    );
  } catch (e) {
    // Ignore logout errors
  }

  commit('updateUser', toPlain(defaultUserModel));
}

// Resolves once the list types are in the store
export function fetchListTypes({ commit }) {
  return new Promise((resolve, reject) => {
    const { onResult, onError } = useQuery(getListTypes, {
      first: 1000,
    }, {
      fetchPolicy: 'cache-and-network',
    });
    onResult((res) => {
      commit('updateListTypes', res.data.listTypes.data);
      resolve();
    });
    onError(reject);
  });
}

export function drawerOpen({ commit }, open) {
  commit('setDrawerOpen', open);
}

export async function addListItem(ctx, payload) {
  const { mutate } = useMutation(createListItem);

  /* eslint-disable */
  const listTypeId = ctx.getters.listTypes.find((el) => el.name === payload.listTypeName).id;

  try {
    const res = await mutate({
      input: {
        name: payload.name,
        list_type_id: listTypeId,
        description: '',
      },
    });

    return res.data.createListItem;
  } catch (e) {
    toast.danger('Error creating item', e.message)
  }

  return null;
}

export const saveLastCaseType = ({ commit }, value) => {
  commit('setLastCaseType', value)
}
